// Round-by-round points table shown on the LEFT of the game table.
//   Rounds | Player-A | Player-B | Player-C
//     1    |   35     |    0     |   48
//     2    |  out     |    2     |   10
//   Total  | 115(e)   |    4     |   58
// Data comes from the engine's per-deal history ('get-deal-history'); the Total row uses the live
// cumulative scores. A player is shown as "out" in the rounds after the one that eliminated them.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const shortName = (name) => {
  const s = String(name || 'Player');
  return s.length > 7 ? `${s.slice(0, 6)}…` : s;
};

export default function RoundPointsPanel({ socket, gameId, gameState, isMobile = false, currentUserId = null, open = true, onClose = null }) {
  const [history, setHistory] = useState([]);

  const status = gameState?.status;
  const currentDeal = gameState?.currentDeal || 1;

  // Refresh whenever a round starts / finishes
  useEffect(() => {
    if (!socket || !gameId || !open) return undefined;
    let cancelled = false;
    socket.emit('get-deal-history', { gameId }, (res) => {
      if (!cancelled && res && res.success && Array.isArray(res.history)) setHistory(res.history);
    });
    return () => { cancelled = true; };
  }, [socket, gameId, currentDeal, status, open]);

  const view = useMemo(() => {
    const players = Array.isArray(gameState?.players) ? gameState.players : [];
    const maxPoints = Number(gameState?.maxPoints) || 0;
    const isPoints = gameState?.gameMode === 'points';
    const completed = [...history].sort((a, b) => a.deal - b.deal);

    // Round in which each player was eliminated (their points are still shown for that round)
    const outRound = {};
    completed.forEach((h) => (h.pointTable || []).forEach((row) => {
      const id = String(row.userId);
      if (row.eliminated && outRound[id] === undefined) outRound[id] = h.deal;
    }));

    const rows = completed.map((h) => ({
      round: h.deal,
      cells: players.map((p) => {
        const id = String(p.userId);
        if (outRound[id] !== undefined && h.deal > outRound[id]) return { out: true };
        const row = (h.pointTable || []).find((r) => String(r.userId) === id);
        if (!row) return { dash: true };
        return { value: row.pointsThisGame === null || row.pointsThisGame === undefined ? 0 : row.pointsThisGame };
      })
    }));

    const roundInProgress = !['deal_completed', 'finished', 'cancelled'].includes(status) &&
      !completed.some((h) => h.deal === currentDeal);
    if (roundInProgress) {
      rows.push({ round: currentDeal, pending: true, cells: players.map(() => ({ pending: true })) });
    }

    const totals = players.map((p) => {
      const id = String(p.userId);
      const fromHistory = completed.reduce((sum, h) => {
        const row = (h.pointTable || []).find((r) => String(r.userId) === id);
        return sum + (row && row.pointsThisGame ? row.pointsThisGame : 0);
      }, 0);
      const total = (p.cumulativeScore !== undefined && p.cumulativeScore !== null) ? p.cumulativeScore : fromHistory;
      const eliminated = outRound[id] !== undefined || p.eliminated === true ||
        (isPoints && maxPoints > 0 && total >= maxPoints);
      return { total, eliminated };
    });

    return { players, rows, totals };
  }, [history, gameState, status, currentDeal]);

  if (!gameState || view.players.length === 0) return null;
  if (!open || status === 'waiting' || status === 'seating') return null;

  const font = isMobile ? 10 : 12;
  const cell = { padding: isMobile ? '2px 4px' : '3px 7px', textAlign: 'center', whiteSpace: 'nowrap', fontSize: font };

  const toggle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffd54f', fontSize: font, fontWeight: 700, textShadow: '0 1px 4px #000' }}>
      <span>📋 Round points</span>
      {onClose && (
        <button
          onClick={onClose}
          title="Close"
          style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: font, lineHeight: 1, padding: '1px 6px' }}
        >✕</button>
      )}
    </div>
  );

  const panel = (
    <div
      className="round-points-panel"
      style={{
        position: 'fixed', left: isMobile ? 46 : 62, top: '50%', transform: 'translateY(-50%)', zIndex: 1500, display: 'flex',
        flexDirection: 'column', alignItems: 'flex-start', gap: 4, pointerEvents: 'auto'
      }}
    >
      {toggle}
      {open && (
        <div style={{
          maxHeight: isMobile ? '38vh' : '42vh', maxWidth: isMobile ? '58vw' : '40vw', overflow: 'auto',
          background: 'rgba(8, 14, 28, 0.78)', border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 8, backdropFilter: 'blur(3px)', color: '#fff'
        }}>
          <table style={{ borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.08)' }}>
                <th style={{ ...cell, color: '#ffd54f' }}>Rounds</th>
                {view.players.map((p) => {
                  const mine = String(p.userId) === String(currentUserId);
                  return (
                    <th key={p.userId} title={p.username} style={{ ...cell, color: mine ? '#00e676' : '#ffd54f' }}>
                      {shortName(p.username)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.round} style={{ borderTop: '1px solid rgba(255,255,255,0.08)', opacity: r.pending ? 0.6 : 1 }}>
                  <td style={{ ...cell, color: '#bbb' }}>{r.round}</td>
                  {r.cells.map((c, i) => (
                    <td key={i} style={{ ...cell, color: c.out ? '#ff6e6e' : (c.value === 0 ? '#00e676' : '#fff'), fontWeight: c.out || c.value === 0 ? 700 : 400 }}>
                      {c.out ? 'out' : (c.pending ? '…' : (c.dash ? '-' : c.value))}
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)' }}>
                <td style={{ ...cell, color: '#ffd54f', fontWeight: 700 }}>Total</td>
                {view.totals.map((t, i) => (
                  <td key={i} style={{ ...cell, fontWeight: 700, color: t.eliminated ? '#ff6e6e' : '#fff' }}>
                    {t.total}{t.eliminated ? '(e)' : ''}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}

